import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download, Lock } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { addMonths, downloadCSV, monthLabel, STATUS_LABEL, statusCode, statusTone, todayISO, WEEKDAYS } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRMonth } from '@/types/hr'
import { Empty } from './shared'

// The month at a glance (F-HR-N2), laid out like the Excel sheets tenants
// keep today: one row per person, one column per day, a code in each cell
// and the totals on the right. Faded codes are filled in automatically
// (weekly off, holiday); click a day to open it in the register.

export function MonthTab({ month: monthProp, onMonth, onOpenDay }: { month?: string; onMonth: (m: string) => void; onOpenDay: (d: string) => void }) {
  const month = monthProp && /^\d{4}-\d{2}$/.test(monthProp) ? monthProp : todayISO().slice(0, 7)
  const { data, isLoading } = useQuery({ queryKey: ['hr', 'month', month], queryFn: () => apiClient.hrGetMonth(month) })
  const m = data?.data as HRMonth | undefined
  const today = todayISO()

  const exportCSV = () => {
    if (!m) return
    const header = ['Name', 'Department', ...m.days.map((d) => d.date.slice(8)), 'Worked', 'Absent', 'Late', 'Half days', 'Paid leave', 'Unpaid leave', 'Off', 'Holidays', 'Not marked']
    const rows = m.people.map((p) => [
      p.full_name,
      p.department,
      ...m.days.map((d) => {
        const c = p.cells[d.date]
        return c ? statusCode(c.s, c.lt) + (c.s === 'late' && c.m ? ` ${c.m}m` : '') : ''
      }),
      p.totals.worked,
      p.totals.absent,
      p.totals.late,
      p.totals.half_days,
      p.totals.paid_leave,
      p.totals.unpaid_leave,
      p.totals.off,
      p.totals.holidays,
      p.totals.unmarked,
    ])
    downloadCSV(`attendance-${month}.csv`, [header, ...rows])
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onMonth(addMonths(month, -1))} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="w-36 text-center text-sm font-medium">{monthLabel(month)}</span>
          <Button variant="outline" size="sm" onClick={() => onMonth(addMonths(month, 1))} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {m?.locked && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Locked by payroll
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-muted-foreground md:inline">
            P present · L late · H half day · A absent · CL/SL/AL leave · O off · HO holiday
          </span>
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!m?.people.length}>
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !m || m.people.length === 0 ? (
        <Empty>Nobody was employed in {monthLabel(month)}.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-muted/40">
                <th className="sticky left-0 z-10 min-w-[160px] border-b bg-muted/40 px-3 py-2 text-left font-medium">Person</th>
                {m.days.map((d) => (
                  <th
                    key={d.date}
                    className={cn('min-w-[30px] border-b px-0.5 py-1 text-center font-medium', d.holiday && 'text-sky-700 dark:text-sky-300', d.date === today && 'bg-background')}
                    title={d.holiday ?? undefined}
                  >
                    <button type="button" className="w-full hover:underline" onClick={() => onOpenDay(d.date)}>
                      <div>{Number(d.date.slice(8))}</div>
                      <div className="font-normal text-muted-foreground">{WEEKDAYS[d.weekday].slice(0, 2)}</div>
                    </button>
                  </th>
                ))}
                {['Worked', 'Abs', 'Late', 'Leave', 'Unpaid', 'Not marked'].map((h) => (
                  <th key={h} className="border-b border-l px-2 py-2 text-right font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {m.people.map((p) => (
                <tr key={p.employee_id} className="border-b last:border-0">
                  <td className="sticky left-0 z-10 bg-background px-3 py-1.5">
                    <div className="truncate font-medium">{p.full_name}</div>
                    <div className="truncate text-muted-foreground">{p.department}</div>
                  </td>
                  {m.days.map((d) => {
                    const c = p.cells[d.date]
                    const code = c ? statusCode(c.s, c.lt) : ''
                    const title = c
                      ? `${STATUS_LABEL[c.s]}${c.lt ? ` (${c.lt})` : ''}${c.m ? ` · ${c.m} min late` : ''}${c.i ? ' · automatic' : ''}${c.n ? ` · ${c.n}` : ''}`
                      : d.date > today
                        ? ''
                        : 'Not marked'
                    return (
                      <td key={d.date} className="px-0.5 py-1 text-center" title={title}>
                        {c ? (
                          <span className={cn('inline-block min-w-[24px] rounded px-0.5 py-0.5 font-medium', statusTone(c.s), c.i && 'opacity-60')}>{code}</span>
                        ) : d.date <= today && d.date >= p.join_date && (!p.exit_date || d.date <= p.exit_date) ? (
                          <span className="text-amber-600">·</span>
                        ) : null}
                      </td>
                    )
                  })}
                  <td className="border-l px-2 text-right tabular-nums">{p.totals.worked}</td>
                  <td className={cn('px-2 text-right tabular-nums', p.totals.absent > 0 && 'font-semibold text-red-700 dark:text-red-300')}>{p.totals.absent}</td>
                  <td className="px-2 text-right tabular-nums">{p.totals.late}</td>
                  <td className="px-2 text-right tabular-nums">{p.totals.paid_leave}</td>
                  <td className="px-2 text-right tabular-nums">{p.totals.unpaid_leave}</td>
                  <td className={cn('px-2 text-right tabular-nums', p.totals.unmarked > 0 && 'font-semibold text-amber-700 dark:text-amber-300')}>{p.totals.unmarked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
